// Starting or reopening a direct chat (17-people.md): an existing chat with the person is reused
// (one listed for them first); otherwise an encrypted direct chat is made and listed in m.direct.
import { EventType, type MatrixClient } from 'matrix-js-sdk';
import { describe } from '../core/errors';
import { navigate } from '../core/router';
import { t } from '../i18n';
import { makeRoom } from '../frame/create';

const directMap = (client: MatrixClient) => client.getAccountData(EventType.Direct)?.getContent<Record<string, string[]>>() ?? {};

/** A joined direct chat with the person (17, step 1): listed for them, they joined or invited, two people at most. */
export function findDirectChat(client: MatrixClient, userId: string): string | undefined {
  const map = directMap(client);
  const listed = new Set(Object.values(map).flat());
  const candidates = client.getRooms().filter((r) => listed.has(r.roomId) && r.getMyMembership() === 'join' && r.getInvitedAndJoinedMemberCount() <= 2);
  const fits = (roomId: string) => {
    const room = candidates.find((r) => r.roomId === roomId);
    const membership = room?.getMember(userId)?.membership;
    return room && membership !== 'leave' && membership !== 'ban';
  };
  return (map[userId] ?? []).find(fits) ?? candidates.find((r) => ['join', 'invite'].includes(r.getMember(userId)?.membership ?? ''))?.roomId;
}

let starting = false;

/** Opens the chat with a person, making it when there is none (never two at once). */
export async function openDirectChat(client: MatrixClient, userId: string, name: string): Promise<void> {
  const found = findDirectChat(client, userId);
  if (found) return navigate(`/direct/${encodeURIComponent(found)}/`);
  if (starting) return;
  starting = true;
  const at = location.pathname;
  try {
    const roomId = await makeRoom(client, { name: '', kind: 'text', encrypted: true, invite: [userId] });
    await listDirect(client, roomId, userId);
    // Left meanwhile (the browser's Back): it shows in Direct messages but doesn't open (07, v1.58.38).
    if (location.pathname === at) navigate(`/direct/${encodeURIComponent(roomId)}/`);
  } catch (e) {
    throw new Error(describe(e, 'general').kind === 'RL' ? t('people.tooManyChats') : t('people.chatFailed', { name }));
  } finally {
    starting = false;
  }
}

/** Lists a chat in m.direct under the person (a new chat, an accepted direct-chat invitation). */
export async function listDirect(client: MatrixClient, roomId: string, userId: string): Promise<void> {
  const map = directMap(client);
  const next = Object.fromEntries(Object.entries(map).map(([u, rooms]) => [u, rooms.filter((r) => r !== roomId)]));
  next[userId] = [...(next[userId] ?? []), roomId];
  await client.setAccountData(EventType.Direct, next as never);
}
